import { useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, X, Play } from "lucide-react";
import { motion, StaggerGrid, StaggerItem, FadeUp } from "@/lib/motion";

const BG = {
  white: "bg-white",
  muted: "bg-slate-50/60",
  accent_red: "bg-gradient-to-b from-red-50/60 via-white to-white",
  dark: "bg-slate-950 text-white",
};

function isYouTube(url = "") {
  return /(?:youtube\.com|youtu\.be)/i.test(url);
}
function isVimeo(url = "") {
  return /vimeo\.com/i.test(url);
}
function ytEmbed(url) {
  const m = url.match(/(?:v=|youtu\.be\/|embed\/)([\w-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : url;
}
function vimeoEmbed(url) {
  const m = url.match(/vimeo\.com\/(\d+)/);
  return m ? `https://player.vimeo.com/video/${m[1]}` : url;
}

/** Renders a section header (overline, title, subtitle) */
function SectionHead({ overline, title, subtitle, dark }) {
  if (!overline && !title && !subtitle) return null;
  return (
    <FadeUp className="max-w-2xl mb-10">
      {overline && <div className={`overline mb-3 ${dark ? "!text-red-400" : ""}`}>{overline}</div>}
      {title && <h2 className={`text-3xl lg:text-4xl font-heading font-semibold ${dark ? "text-white" : "text-slate-900"}`}>{title}</h2>}
      {subtitle && <p className={`mt-4 ${dark ? "text-slate-300" : "text-slate-600"} text-lg`}>{subtitle}</p>}
    </FadeUp>
  );
}

/** Renders a CTA button */
function CTAButton({ label, url, dark }) {
  if (!label || !url) return null;
  const external = /^https?:\/\//i.test(url);
  const cls = dark
    ? "inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-slate-900 hover:bg-slate-100 text-sm font-medium transition"
    : "inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-red-700 hover:bg-red-800 text-white text-sm font-medium transition";
  return (
    <a href={url} target={external ? "_blank" : undefined} rel={external ? "noreferrer" : undefined} className={cls}>
      {label}
    </a>
  );
}

/** Video player — YouTube/Vimeo iframe or native MP4 */
function VideoPlayer({ url, poster, className = "" }) {
  if (!url) return null;
  if (isYouTube(url)) {
    return <iframe src={ytEmbed(url)} title="video" className={`w-full aspect-video rounded-2xl ${className}`} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />;
  }
  if (isVimeo(url)) {
    return <iframe src={vimeoEmbed(url)} title="video" className={`w-full aspect-video rounded-2xl ${className}`} allow="autoplay; fullscreen; picture-in-picture" allowFullScreen />;
  }
  return <video src={url} poster={poster} controls className={`w-full aspect-video rounded-2xl bg-black ${className}`} />;
}

/** Item card used inside grid / slider layouts */
function ItemCard({ item, dark }) {
  const hasCta = item.cta_label && item.cta_url;
  return (
    <div className={`card-medi h-full flex flex-col ${dark ? "!bg-slate-900 !border-slate-800" : ""}`}>
      {item.video_url ? (
        <VideoPlayer url={item.video_url} className="rounded-none" />
      ) : item.image_url ? (
        <div className="aspect-[4/3] overflow-hidden bg-slate-100">
          <img src={item.image_url} alt={item.title || "Item"} className="w-full h-full object-cover hover:scale-105 transition duration-500" loading="lazy" />
        </div>
      ) : null}
      <div className="p-5 flex-1 flex flex-col">
        {item.title && <h3 className={`font-heading font-semibold text-lg ${dark ? "text-white" : "text-slate-900"}`}>{item.title}</h3>}
        {item.caption && <p className={`text-sm mt-1.5 leading-relaxed flex-1 ${dark ? "text-slate-400" : "text-slate-600"}`}>{item.caption}</p>}
        {hasCta && <div className="mt-4"><CTAButton {...{ label: item.cta_label, url: item.cta_url, dark }} /></div>}
      </div>
    </div>
  );
}

/** Grid renderer for two_col / three_col / four_col */
function GridSection({ section, cols }) {
  const dark = section.background === "dark";
  const gridCls = { 1: "grid-cols-1", 2: "sm:grid-cols-2", 3: "sm:grid-cols-2 lg:grid-cols-3", 4: "sm:grid-cols-2 lg:grid-cols-4" }[cols];
  return (
    <StaggerGrid className={`grid gap-6 ${gridCls}`}>
      {section.items.map((it, i) => (
        <StaggerItem key={it.id} index={i}><ItemCard item={it} dark={dark} /></StaggerItem>
      ))}
    </StaggerGrid>
  );
}

/** Single full-width — big banner-esque card */
function SingleSection({ section }) {
  const it = section.items[0];
  if (!it) return null;
  const dark = section.background === "dark";
  return (
    <div className="grid md:grid-cols-2 gap-8 items-center">
      {(it.image_url || it.video_url) && (
        <div className="rounded-3xl overflow-hidden shadow-medi-lg aspect-video bg-slate-100">
          {it.video_url ? <VideoPlayer url={it.video_url} className="rounded-none aspect-auto h-full" /> : <img src={it.image_url} alt={it.title || "banner"} className="w-full h-full object-cover" />}
        </div>
      )}
      <div>
        {it.title && <h3 className={`text-2xl lg:text-3xl font-heading font-semibold ${dark ? "text-white" : "text-slate-900"}`}>{it.title}</h3>}
        {it.caption && <p className={`mt-3 leading-relaxed ${dark ? "text-slate-300" : "text-slate-600"}`}>{it.caption}</p>}
        <div className="mt-6"><CTAButton label={it.cta_label} url={it.cta_url} dark={dark} /></div>
      </div>
    </div>
  );
}

/** Banner — full-bleed image with text overlay */
function BannerSection({ section }) {
  const it = section.items[0];
  if (!it) return null;
  return (
    <div className="relative rounded-3xl overflow-hidden aspect-[21/9] shadow-medi-xl">
      {it.image_url && <img src={it.image_url} alt={it.title || "banner"} className="absolute inset-0 w-full h-full object-cover" />}
      <div className="absolute inset-0 bg-gradient-to-r from-slate-950/80 via-slate-950/50 to-slate-950/10" />
      <div className="relative h-full flex flex-col justify-center p-8 lg:p-16 max-w-2xl text-white">
        {section.overline && <div className="overline !text-red-300 mb-3">{section.overline}</div>}
        {it.title && <h3 className="text-3xl lg:text-5xl font-heading font-semibold leading-tight">{it.title}</h3>}
        {it.caption && <p className="mt-4 text-white/85 text-lg leading-relaxed">{it.caption}</p>}
        <div className="mt-6"><CTAButton label={it.cta_label} url={it.cta_url} dark /></div>
      </div>
    </div>
  );
}

/** Video-only section — single big embed */
function VideoSection({ section }) {
  const it = section.items[0];
  if (!it?.video_url) return null;
  return (
    <div className="max-w-4xl mx-auto rounded-3xl overflow-hidden shadow-medi-xl">
      <VideoPlayer url={it.video_url} className="rounded-none" />
      {(it.title || it.caption) && (
        <div className="p-6 text-center bg-white">
          {it.title && <h3 className="font-heading font-semibold text-lg text-slate-900">{it.title}</h3>}
          {it.caption && <p className="text-sm text-slate-600 mt-1">{it.caption}</p>}
        </div>
      )}
    </div>
  );
}

/** Slider — embla carousel with arrows + dots */
function SliderSection({ section }) {
  const [emblaRef, embla] = useEmblaCarousel({ loop: true, align: "start" });
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (!embla) return;
    const onSelect = () => setSelectedIndex(embla.selectedScrollSnap());
    embla.on("select", onSelect);
    onSelect();
    const timer = setInterval(() => embla.scrollNext(), 5000);
    return () => { embla.off("select", onSelect); clearInterval(timer); };
  }, [embla]);

  const dark = section.background === "dark";

  return (
    <div className="relative">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex gap-6">
          {section.items.map((it) => (
            <div key={it.id} className="min-w-0 flex-[0_0_100%] sm:flex-[0_0_60%] lg:flex-[0_0_45%]">
              <ItemCard item={it} dark={dark} />
            </div>
          ))}
        </div>
      </div>

      {section.items.length > 1 && (
        <>
          <button onClick={() => embla?.scrollPrev()} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-6 w-11 h-11 rounded-full bg-white shadow-medi-lg text-slate-700 hover:bg-red-50 hover:text-red-700 grid place-items-center transition" aria-label="Prev">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button onClick={() => embla?.scrollNext()} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-6 w-11 h-11 rounded-full bg-white shadow-medi-lg text-slate-700 hover:bg-red-50 hover:text-red-700 grid place-items-center transition" aria-label="Next">
            <ChevronRight className="w-5 h-5" />
          </button>
          <div className="flex justify-center gap-2 mt-6">
            {section.items.map((_, i) => (
              <button key={i} onClick={() => embla?.scrollTo(i)} className={`h-2 rounded-full transition-all ${i === selectedIndex ? "w-8 bg-red-700" : "w-2 bg-slate-300 hover:bg-slate-400"}`} aria-label={`Slide ${i + 1}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** Gallery — masonry-ish grid with lightbox */
function GallerySection({ section }) {
  const [open, setOpen] = useState(null);
  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {section.items.map((it, i) => (
          <motion.button
            key={it.id}
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.5, delay: Math.min(i * 0.05, 0.4) }}
            onClick={() => setOpen(it)}
            className={`relative overflow-hidden rounded-2xl bg-slate-100 group ${i % 5 === 0 ? "row-span-2 aspect-square" : "aspect-square"}`}
          >
            {it.video_url ? (
              <div className="w-full h-full grid place-items-center bg-slate-900 text-white">
                <Play className="w-8 h-8" />
              </div>
            ) : (
              <img src={it.image_url} alt={it.title || "Gallery"} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" loading="lazy" />
            )}
            {it.title && (
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-3">
                <div className="text-white text-sm font-medium">{it.title}</div>
              </div>
            )}
          </motion.button>
        ))}
      </div>
      {open && (
        <div onClick={() => setOpen(null)} className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm grid place-items-center p-4">
          <button onClick={() => setOpen(null)} className="absolute top-6 right-6 text-white/80 hover:text-white p-2"><X className="w-6 h-6" /></button>
          {open.video_url ? (
            <VideoPlayer url={open.video_url} className="max-w-4xl w-full" />
          ) : (
            <img src={open.image_url} alt={open.title || "Preview"} className="max-h-[85vh] max-w-[90vw] rounded-2xl" />
          )}
          {open.title && <div className="absolute bottom-6 text-white text-center">{open.title}</div>}
        </div>
      )}
    </>
  );
}

/** Main renderer — decides which sub-renderer to use */
export function HomeSectionRenderer({ section }) {
  if (!section?.items?.length && section.layout !== "video") return null;
  const dark = section.background === "dark";
  const bgCls = BG[section.background] || BG.white;

  const renderInner = () => {
    switch (section.layout) {
      case "single":     return <SingleSection section={section} />;
      case "two_col":    return <GridSection section={section} cols={2} />;
      case "three_col":  return <GridSection section={section} cols={3} />;
      case "four_col":   return <GridSection section={section} cols={4} />;
      case "slider":     return <SliderSection section={section} />;
      case "gallery":    return <GallerySection section={section} />;
      case "video":      return <VideoSection section={section} />;
      case "banner":     return <BannerSection section={section} />;
      default:           return <GridSection section={section} cols={3} />;
    }
  };

  return (
    <section className={`section ${bgCls}`} data-testid={`home-section-${section.id}`}>
      <div className="container-medi">
        {section.layout !== "banner" && <SectionHead overline={section.overline} title={section.title} subtitle={section.subtitle} dark={dark} />}
        {renderInner()}
      </div>
    </section>
  );
}

/** Renders all dynamic sections that match a given position slot */
export function DynamicSlot({ position, sections }) {
  const matches = (sections || []).filter((s) => s.position === position && s.enabled);
  if (matches.length === 0) return null;
  return (
    <>
      {matches.map((s) => <HomeSectionRenderer key={s.id} section={s} />)}
    </>
  );
}
